import React, { useState } from "react";
import { Paper, Button, CircularProgress } from "@mui/material";
import styled from "styled-components";
import { Edit2 } from "react-feather";
import API from "../../../../axios/instances/API";
import useGlobalStore from "src/store/useGlobalStore";

const MAX_CHARS = 500;

const Wrapper = styled(Paper)`
  border-radius: 6px;
  width: 100%;
  min-width: 280px;
  padding: 16px;
  display: flex;
  flex-direction: column;
`;

const Label = styled.p`
  font-size: 11px;
  font-weight: 600;
  color: slategray;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
`;

const ReadonlyBox = styled.div`
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 12px 14px;
  min-height: 48px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  cursor: pointer;
  background: #fafafa;
  transition: border-color 0.15s;

  &:hover {
    border-color: #bdbdbd;
    background: #f5f5f5;
  }
`;

const PlaceholderText = styled.span`
  font-size: 14px;
  color: #9e9e9e;
  flex: 1;
`;

const NoteText = styled.span`
  font-size: 14px;
  color: #333;
  flex: 1;
  white-space: pre-wrap;
  word-break: break-word;
`;

const StyledTextarea = styled.textarea`
  width: 100%;
  min-height: 100px;
  border: 1px solid #90caf9;
  border-radius: 6px;
  padding: 10px 12px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  outline: none;
  box-sizing: border-box;
  transition: border-color 0.15s;

  &:focus {
    border-color: #1976d2;
  }
`;

const CharCount = styled.p`
  font-size: 12px;
  color: ${({ $over }) => ($over ? "#d32f2f" : "#9e9e9e")};
  text-align: right;
  margin-top: 4px;
  margin-bottom: 8px;
`;

const ActionRow = styled.div`
  display: flex;
  gap: 8px;
`;

const MemberNotes = ({ data, mutate, readOnly }) => {
  const setSnackbarText = useGlobalStore((state) => state.setSnackbarText);
  const [editing, setEditing] = useState(false);
  const [noteValue, setNoteValue] = useState("");
  const [saving, setSaving] = useState(false);

  const existingNote = data?.user?.notes ?? null;
  const teamMemberId = data?.team_member?.id;

  const trimmedValue = noteValue.trim();
  const isSaveDisabled =
    saving || !trimmedValue || trimmedValue === (existingNote ?? "").trim();

  const handleClickReadonly = () => {
    if (readOnly) return;
    setNoteValue(existingNote ?? "");
    setEditing(true);
  };

  const handleCancel = () => {
    setEditing(false);
    setNoteValue("");
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await API.patch("dashboard/teams/details/", {
        id: teamMemberId,
        notes: noteValue.trim(),
      });
      await mutate();
      setEditing(false);
      setNoteValue("");
      setSnackbarText("Note saved.");
    } catch (_) {
      setSnackbarText("Failed to save note. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Wrapper>
      <Label>Notes</Label>
      {editing ? (
        <>
          <StyledTextarea
            autoFocus
            value={noteValue}
            onChange={(e) => setNoteValue(e.target.value.slice(0, MAX_CHARS))}
            placeholder="Add your notes here."
            maxLength={MAX_CHARS}
          />
          <CharCount $over={noteValue.length >= MAX_CHARS}>
            {noteValue.length}/{MAX_CHARS}
          </CharCount>
          <ActionRow>
            <Button
              variant="outlined"
              fullWidth
              size="small"
              onClick={handleCancel}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              fullWidth
              size="small"
              onClick={handleSave}
              disabled={isSaveDisabled}
            >
              {saving ? <CircularProgress size={16} color="inherit" /> : "Save"}
            </Button>
          </ActionRow>
        </>
      ) : (
        <ReadonlyBox onClick={handleClickReadonly}>
          {existingNote ? (
            <NoteText>{existingNote}</NoteText>
          ) : (
            <PlaceholderText>Add your notes here.</PlaceholderText>
          )}
          {!readOnly && <Edit2 size={14} color="#9e9e9e" />}
        </ReadonlyBox>
      )}
    </Wrapper>
  );
};

export default MemberNotes;
